/**
 * editor2/constants.ts
 * ReservationEditor v2 の定数定義
 * ReservationEditorPanel.tsx から抽出
 */
import { getInstanceConfig } from '../../config/instances';

// --- 自動変更メモ フィールド設定 (75+ fields) ---
export const AUTO_MEMO_FIELD_CONFIGS = [
  // ステータス
  { field: 'status', label: 'ステータス', category: 'other', formatByStatus: true },
  // 日時
  { field: 'start_date', label: 'レンタル開始', category: 'other', formatDate: true },
  { field: 'end_date', label: 'レンタル終了', category: 'other', formatDate: true },
  { field: 'request_coming_time', label: '来店希望時刻', category: 'other' },
  { field: 'request_returning_time', label: '返却希望時刻', category: 'other' },
  // 車両
  { field: 'car_type', label: '車種', category: 'other' },
  { field: 'vehicle_id', label: '車両ID', category: 'other' },
  // 顧客情報
  { field: 'customer_name', label: '顧客名', category: 'reservation' },
  { field: 'customer_email', label: 'メール', category: 'reservation' },
  { field: 'customer_tel', label: '電話番号', category: 'reservation' },
  { field: 'customer_address', label: '住所', category: 'reservation' },
  // 乗車人数
  { field: 'pax', label: '乗車人数', category: 'reservation' },
  { field: 'pax_children', label: '子供人数', category: 'reservation' },
  { field: 'child_seat', label: 'チャイルドシート', category: 'reservation' },
  { field: 'booster_seat', label: 'ブースターシート', category: 'reservation' },
  { field: 'junior_seat', label: 'ジュニアシート', category: 'reservation' },
  // フライト
  { field: 'arrival_flight', label: '到着便', category: 'reservation' },
  { field: 'departure_flight', label: '出発便', category: 'reservation' },
  // 媒体・プラン
  { field: 'contact_media', label: '予約媒体', category: 'reservation' },
  { field: 'contact_plan', label: 'プラン', category: 'reservation' },
  // 料金
  { field: 'base_price', label: '基本料金', category: 'payment', formatPrice: true },
  { field: 'option_price', label: 'オプション料金', category: 'payment', formatPrice: true },
  { field: 'noc_price', label: 'NOC料金', category: 'insurance', formatPrice: true },
  { field: 'cdw_price', label: 'CDW料金', category: 'insurance', formatPrice: true },
  { field: 'sum_price', label: '合計金額', category: 'payment', formatPrice: true },
  { field: 'coupon', label: 'クーポン', category: 'payment' },
  { field: 'point', label: 'ポイント', category: 'payment' },
  { field: 'days', label: '日数', category: 'other' },
  { field: 'nights', label: '泊数', category: 'other' },
  // NOC/CDW トグル
  { field: 'noc', label: 'NOC', category: 'insurance', formatBool: true },
  { field: 'cdw', label: 'CDW', category: 'insurance', formatBool: true },
  // 確認・同意
  { field: 'license', label: '免許証確認', category: 'other', formatCheck: true },
  { field: 'payment', label: '決済確認', category: 'other', formatCheck: true },
  { field: 'terms_and_conditions', label: '利用規約同意', category: 'other', formatCheck: true },
  { field: 'disclaimer', label: '免責同意', category: 'other', formatCheck: true },
  { field: 'agreement', label: '契約同意', category: 'other', formatCheck: true },
  // メール送信フラグ
  { field: 'mail_send_flg1', label: '予約確定メール', category: 'other', formatBool: true },
  { field: 'mail_send_flg2', label: '決済御礼メール', category: 'other', formatBool: true },
  { field: 'mail_send_flg3', label: '利用御礼メール', category: 'other', formatBool: true },
  // 連絡手段
  { field: 'communication_tools', label: '連絡手段', category: 'reservation', formatCommTools: true },
  // 色
  { field: 'bg_color', label: '背景色', category: 'other' },
  { field: 'text_color', label: '文字色', category: 'other' },
  // その他
  { field: 'short_memo', label: 'ショートメモ', category: 'other' },
  { field: 'option', label: 'オプション', category: 'other' },
  { field: 'comment', label: 'コメント', category: 'other' },
  { field: 'invoice_link', label: 'インボイス', category: 'payment' },
] as const;

export type AutoMemoFieldConfig = (typeof AUTO_MEMO_FIELD_CONFIGS)[number];

// --- アクションラベル ---
export const ACTION_LABELS: Record<string, string> = {
  reservation: '予約処理',
  cancel: 'キャンセル処理',
  payment: '決済処理',
  insurance: '補償処理',
  other: 'その他処理',
};

// --- カテゴリ優先度: 決済 > 補償 > 予約 > その他 ---
export const ACTION_PRIORITY = ['payment', 'insurance', 'reservation', 'other'] as const;

// --- カテゴリ → アクション マッピング ---
export const CATEGORY_TO_ACTION: Record<string, string> = {
  cancel: 'cancel',
  payment: 'payment',
  insurance: 'insurance',
  reservation: 'reservation',
  other: 'other',
};

// --- 連絡手段マスタ ---
export const COMMUNICATION_TOOLS_MASTER = [
  { id: 1, name: 'TEL' },
  { id: 2, name: 'LINE' },
  { id: 3, name: 'SMS' },
  { id: 4, name: 'WhatsApp' },
  { id: 5, name: 'Instagram' },
  { id: 6, name: 'その他' },
] as const;

// communication_tools ID → 名前マッピング
export const COMM_TOOL_NAMES: Record<number, string> = {
  1: 'TEL',
  2: 'LINE',
  3: 'SMS',
  4: 'WhatsApp',
  5: 'Instagram',
  6: 'その他',
};

// --- 予約媒体の候補（combobox の datalist 用。フリー入力も可）---
export const CONTACT_MEDIA_OPTIONS = [
  'lp[ja]',
  'lp[en]',
  'lp[tw]',
  'lp[kr]',
  'JA',
  'レンナビ',
  'okiren',
  'エアトリ',
  'rcom',
  '紹介',
  '直来',
  'TEL',
  'LINE',
  'Kakao',
  'WhatsApp',
  'SMS',
  'リピート',
  'その他',
] as const;

// --- メールフッターテンプレート ---
// 店舗ブランド情報(instances.ts の SSOT)から4言語分を生成する。那覇/宮古で
// 店名・住所・TEL・LINE 有無・運営会社が自動的に出し分けられる(F2 の那覇固定を解消)。
const _b = getInstanceConfig().branding;
// 言語別ラベル(LINE 見出し・WEBサイト見出し)。ja は国内 TEL、他言語は国際 TEL を使う。
const _footerParts: Record<string, { tel: string; lineLabel: string; siteLabel: string }> = {
  ja: { tel: _b.tel,     lineLabel: 'LINE（緊急対応）', siteLabel: `${_b.storeName}WEBサイト` },
  en: { tel: _b.telIntl, lineLabel: 'LINE (Emergency)',  siteLabel: `${_b.storeNameEn} Website` },
  tw: { tel: _b.telIntl, lineLabel: 'LINE（緊急應對）',  siteLabel: `${_b.storeNameEn} 官方網站` },
  kr: { tel: _b.telIntl, lineLabel: 'LINE (긴급 대응)',  siteLabel: `${_b.storeNameEn} 웹사이트` },
};

function buildMailFooter(lang: string): string {
  const p = _footerParts[lang] || _footerParts.ja;
  const lines = [
    '--------------------',
    _b.storeName,
    _b.storeNameEn,
    '',
    _b.postalAddress,
    _b.addressEn,
    '',
    `TEL (${_b.telHours}): ${p.tel}`,
  ];
  if (_b.lineUrl) lines.push(`${p.lineLabel}: ${_b.lineUrl}`);
  lines.push('', `${p.siteLabel}: ${_b.website}`, _b.operatorLine, '--------------------');
  return lines.join('\n');
}

export const MAIL_FOOTER_TEMPLATES: Record<string, string> = {
  ja: buildMailFooter('ja'),
  en: buildMailFooter('en'),
  tw: buildMailFooter('tw'),
  kr: buildMailFooter('kr'),
};

// --- メールテンプレートタイプ ---
export const EMAIL_TEMPLATE_TYPES = [
  'payment',
  'free',
  'request_ok',
  'request_ok_night',
  'request_full',
  'request_offer',
  'cancel',
  'cancel_fee',
  'comment',
] as const;

export type EmailTemplateType = (typeof EMAIL_TEMPLATE_TYPES)[number];

// --- SAFE_DEFAULTS: editing が null の場合のフォールバック ---
export const SAFE_DEFAULTS = {
  id: '',
  contact_id: '',
  contact_code: '',
  page_id: '',
  customer_name: '',
  customer_email: '',
  customer_tel: '',
  customer_address: '',
  start_date: '',
  end_date: '',
  resourceId: '',
  resource_id: '',
  vehicle_id: '',
  vehicle_code: '',
  car_type: '',
  status: null as string | null,
  contact_media: '',
  arrival_flight: '',
  departure_flight: '',
  contact_plan: '',
  pax: 0,
  pax_children: 0,
  booster_seat: 0,
  child_seat: 0,
  junior_seat: 0,
  invoice_link: '',
  comment: '',
  memo: '',
  option: '',
  base_price: null as number | null,
  option_price: null as number | null,
  noc: false,
  noc_price: null as number | null,
  cdw: false,
  cdw_price: null as number | null,
  sum_price: null as number | null,
  days: null as number | null,
  nights: null as number | null,
  coupon: '',
  point: null as number | null,
  bg_color: '',
  text_color: '',
  booking_date: '',
  past_reservation_count: 0,
  mail_send_flg1: false,
  mail_send_flg2: false,
  mail_send_flg3: false,
  communication_tools: [] as number[],
  communication_tools_display: '',
  emergency_name: '',
  emergency_rel: '',
  emergency_tel: '',
  license: false,
  payment: false,
  terms_and_conditions: false,
  disclaimer: false,
  agreement: false,
  short_memo: '',
  request_coming_time: '',
  request_returning_time: '',
  rental_location: '',
  return_location: '',
  pu_staff_id: null as string | null,
  do_staff_id: null as string | null,
  rent_staff_id: null as string | null,
  return_staff_id: null as string | null,
  json_data: null as any,
  __isNew: false,
  __isCopy: false,
} as const;

// ============================================================
// メールテンプレート種別 → 表示ラベル
// EmailTemplateSection のボタンと、送信後の予約メモ記録で共有する。
// (メモに「メール送信済み（決済御礼）」のように内容を残すため)
// ============================================================
export const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  ai_compose: 'AIで作成',
  payment: '決済御礼',
  free: 'フリー',
  request_ok: 'リクエスト OK',
  request_ok_night: 'リクエスト（深夜早朝）OK',
  request_full: 'リクエスト full',
  request_offer: 'リクエスト offer',
  cancel: 'キャンセル（通常）',
  cancel_fee: 'キャンセル（C料金）',
  comment: 'comment',
};

/** 種別コードから日本語ラベルを引く。未知の種別はコードをそのまま返す。 */
export function emailTemplateLabel(type?: string | null): string {
  if (!type) return 'メール';
  return EMAIL_TEMPLATE_LABELS[type] || type;
}
